import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Cluster, Input, Select, Stack, useToast } from "@salesapp/ui";
import { reportSaveBlocked, reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import {
  opsApi,
  type CycleTemplate,
  type CycleTemplateTask,
  type StageEntryRule,
  type Territory,
} from "../../lib/opsApi";
import { FIELD_HINTS, type FieldHintKey } from "../../help/fieldHints";
import {
  ESTIMATE_UNITS,
  ESTIMATE_UNIT_HOURS,
  estimateLabel,
  formatHourSpan,
  isEstimateUnit,
  type EstimateUnit,
} from "./salesCycleEstimate";

const STAGE_LABELS: Record<string, string> = {
  lead_in: "Lead In",
  qualifying: "Qualifying",
  proposal: "Proposal",
  negotiation: "Negotiation",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};

type DraftTask = {
  key: string;
  id?: string;
  title: string;
  is_required: boolean;
  is_active: boolean;
  estimate_amount: number;
  estimate_unit: EstimateUnit;
  deleted: boolean;
};

type DraftStage = {
  id: string;
  name: string;
  sort_order: number;
  tasks: DraftTask[];
};

type DraftTemplate = {
  id: string;
  name: string;
  territory_id: string | null;
  stages: DraftStage[];
};

function stageSlaTooltip(stageName: string): string {
  const key = `cycle_stage_${stageName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")}` as FieldHintKey;
  return (FIELD_HINTS as Record<string, string>)[key] ?? FIELD_HINTS.cycle_stage_generic;
}

function unitOf(value: string | undefined): EstimateUnit {
  return value && isEstimateUnit(value) ? value : "hours";
}

function toDraftTask(task: CycleTemplateTask): DraftTask {
  const unit = unitOf(task.estimate_unit);
  const amount = task.estimate_amount
    ?? Math.max(1, Math.round((task.estimate_hours ?? ESTIMATE_UNIT_HOURS[unit]) / ESTIMATE_UNIT_HOURS[unit]));
  return {
    key: task.id,
    id: task.id,
    title: task.title,
    is_required: task.is_required,
    is_active: task.is_active !== false,
    estimate_amount: amount,
    estimate_unit: unit,
    deleted: false,
  };
}

function toDraft(template: CycleTemplate): DraftTemplate {
  return {
    id: template.id,
    name: template.name,
    territory_id: template.territory_id ?? null,
    stages: template.stages.map((stage, index) => ({
      id: stage.id,
      name: stage.name,
      sort_order: stage.sort_order ?? index + 1,
      tasks: stage.tasks.map(toDraftTask),
    })),
  };
}

function newTask(): DraftTask {
  return {
    key: `new-${crypto.randomUUID()}`,
    title: "",
    is_required: true,
    is_active: true,
    estimate_amount: 1,
    estimate_unit: "days",
    deleted: false,
  };
}

export function SalesCycleSettings({
  orgId,
  canEditDefault = true,
  territoriesOn = true,
}: {
  orgId: string;
  /** Only an organization admin may change the default cycle; app admins edit their own territory's. */
  canEditDefault?: boolean;
  /** A cycle per territory is Enterprise only. */
  territoriesOn?: boolean;
}) {
  const { show } = useToast();
  const [drafts, setDrafts] = useState<DraftTemplate[]>([]);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [territoryId, setTerritoryId] = useState("");
  const [preparing, setPreparing] = useState(false);
  const [rules, setRules] = useState<StageEntryRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingRules, setSavingRules] = useState(false);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  useEffect(() => {
    void opsApi.listCycleTemplates(orgId).then((result) => {
      const incoming = result.templates.map(toDraft);
      setDrafts((current) => {
        const extras = current.filter((item) => !incoming.some((row) => row.id === item.id));
        return [...incoming, ...extras];
      });
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Templates could not be loaded.");
    });
    void opsApi.listTerritories(orgId).then((result) => {
      setTerritories(result.territories.filter((territory) => territory.active !== false));
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Territories could not be loaded.");
    });
    void opsApi.listStageEntryRules(orgId).then((result) => setRules(result.rules)).catch((err) => {
      setError(err instanceof Error ? err.message : "Stage-entry rules could not be loaded.");
    });
  }, [orgId]);

  async function chooseTerritory(next: string) {
    setTerritoryId(next);
    if (!next || drafts.some((item) => item.territory_id === next)) return;
    setPreparing(true);
    setError(null);
    try {
      const created = await opsApi.ensureTerritoryCycle(orgId, next);
      const draft = toDraft(created);
      setDrafts((current) => (current.some((item) => item.id === draft.id) ? current : [...current, draft]));
    } catch (err) {
      setError(err instanceof Error ? err.message : "This territory's cycle could not be opened.");
    } finally {
      setPreparing(false);
    }
  }

  function patchRule(stage: string, next: Partial<StageEntryRule>) {
    setRules((current) => current.map((rule) => (rule.stage === stage ? { ...rule, ...next } : rule)));
  }

  function updateTask(templateId: string, stageId: string, key: string, patch: Partial<DraftTask>) {
    setDirty((current) => ({ ...current, [templateId]: true }));
    setDrafts((current) => current.map((template) => {
      if (template.id !== templateId) return template;
      return {
        ...template,
        stages: template.stages.map((stage) => {
          if (stage.id !== stageId) return stage;
          return {
            ...stage,
            tasks: stage.tasks.map((task) => (task.key === key ? { ...task, ...patch } : task)),
          };
        }),
      };
    }));
  }

  function addTask(templateId: string, stageId: string) {
    setDirty((current) => ({ ...current, [templateId]: true }));
    setDrafts((current) => current.map((template) => {
      if (template.id !== templateId) return template;
      return {
        ...template,
        stages: template.stages.map((stage) => (
          stage.id === stageId ? { ...stage, tasks: [...stage.tasks, newTask()] } : stage
        )),
      };
    }));
  }

  function removeTask(templateId: string, stageId: string, task: DraftTask) {
    setDirty((current) => ({ ...current, [templateId]: true }));
    setDrafts((current) => current.map((template) => {
      if (template.id !== templateId) return template;
      return {
        ...template,
        stages: template.stages.map((stage) => {
          if (stage.id !== stageId) return stage;
          if (!task.id) return { ...stage, tasks: stage.tasks.filter((item) => item.key !== task.key) };
          return {
            ...stage,
            tasks: stage.tasks.map((item) => (item.key === task.key ? { ...item, deleted: true, is_active: false } : item)),
          };
        }),
      };
    }));
  }

  async function saveRules() {
    setSavingRules(true);
    setError(null);
    try {
      const result = await opsApi.saveStageEntryRules(orgId, rules);
      setRules(result.rules);
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Stage-entry rules could not be saved."));
    } finally {
      setSavingRules(false);
    }
  }

  async function saveTemplate(template: DraftTemplate) {
    const missing = template.stages.flatMap((stage) => stage.tasks.filter((task) => !task.deleted && !task.title.trim()));
    if (missing.length > 0) {
      setError(reportSaveBlocked(show, "Each task needs a title before it can be saved."));
      return;
    }
    setSavingId(template.id);
    setError(null);
    try {
      const updated = await opsApi.saveCycleTemplate(orgId, template.id, {
        name: template.name,
        stages: template.stages.map((stage) => ({
          id: stage.id,
          name: stage.name,
          sort_order: stage.sort_order,
          tasks: stage.tasks
            .filter((task) => task.id || !task.deleted)
            .map((task) => ({
              id: task.id,
              title: task.title.trim(),
              is_required: task.is_required,
              is_active: task.is_active && !task.deleted,
              estimate_amount: task.estimate_amount,
              estimate_unit: task.estimate_unit,
              deleted: task.deleted || undefined,
            })),
        })),
      });
      setDrafts((current) => current.map((item) => (item.id === updated.id ? toDraft(updated) : item)));
      setDirty((current) => ({ ...current, [template.id]: false }));
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Sales cycle tasks could not be saved."));
    } finally {
      setSavingId(null);
    }
  }

  const visible = territoryId
    ? drafts.filter((item) => item.territory_id === territoryId)
    : drafts.filter((item) => !item.territory_id);
  const territoryName = territories.find((territory) => territory.id === territoryId)?.name;

  return (
    <Stack gap={4}>
      {error && <p className="crm-error">{error}</p>}
      <p className="home-subtitle">
        Add the tasks your team actually does in each stage, and say how long each one should take.
        The stage length is the sum of its active tasks, and the next stage starts when that time is up.
        Deactivate a task that does not belong in this cycle, or delete it once it is inactive.
        Saving updates open cycles on this plan: new tasks appear, timings move, and tasks nobody has started drop off.
        Work already underway stays on the cycle.
      </p>
      {territoriesOn && (
        <Select
          label="Territory"
          tooltip={FIELD_HINTS.cycle_territory}
          value={territoryId}
          disabled={preparing}
          onChange={(event) => void chooseTerritory(event.target.value)}
        >
          <option value="">Default cycle</option>
          {territories.map((territory) => (
            <option key={territory.id} value={territory.id}>{territory.name}</option>
          ))}
        </Select>
      )}
      <p className="home-subtitle">
        {territoryName
          ? `This cycle belongs to ${territoryName}. Companies in ${territoryName} start on it. Other territories keep their own.`
          : "This is the default cycle. A territory follows it until that territory has its own, and so does a company with no territory."}
      </p>
      {!territoryId && !canEditDefault && (
        <p className="crm-muted">
          Only an organization admin can change the default cycle.
          {territoriesOn ? " Pick your territory above to edit the cycle your team works to." : ""}
        </p>
      )}
      {preparing && <p className="crm-muted">Opening this territory's cycle…</p>}
      {visible.map((template) => (
        <TemplateEditor
          key={template.id}
          template={template}
          saving={savingId === template.id}
          dirty={Boolean(dirty[template.id])}
          readOnly={!template.territory_id && !canEditDefault}
          onAdd={(stageId) => addTask(template.id, stageId)}
          onChange={(stageId, key, patch) => updateTask(template.id, stageId, key, patch)}
          onRemove={(stageId, task) => removeTask(template.id, stageId, task)}
          onSave={() => saveTemplate(template)}
        />
      ))}

      <Card>
        <Stack gap={4}>
          <h2>When a deal enters a stage</h2>
          <p className="home-subtitle">
            Optionally create a cycle task with a due date and queue an email draft. Disabled by default; turn on the stages you want. Use {"{{title}}"} and {"{{stage}}"} in the task or email text.
          </p>
          {rules.map((rule) => (
            <Stack key={rule.stage} gap={2}>
              <strong>{STAGE_LABELS[rule.stage] ?? rule.stage}</strong>
              <Cluster gap={3} align="center">
                <Checkbox
                  checked={rule.enabled}
                  onChange={(event) => patchRule(rule.stage, { enabled: event.target.checked })}
                  label="Enabled"
                  tooltip={FIELD_HINTS.cycle_rule_enabled}
                />
                <Checkbox
                  checked={rule.create_cycle_task}
                  onChange={(event) => patchRule(rule.stage, { create_cycle_task: event.target.checked })}
                  label="Create task"
                  tooltip={FIELD_HINTS.cycle_rule_create_task}
                />
                <Checkbox
                  checked={rule.queue_email_draft}
                  onChange={(event) => patchRule(rule.stage, { queue_email_draft: event.target.checked })}
                  label="Queue email draft"
                  tooltip={FIELD_HINTS.cycle_rule_queue_email_draft}
                />
              </Cluster>
              <Input
                label="Task title"
                tooltip={FIELD_HINTS.cycle_rule_task_title}
                value={rule.task_title}
                onChange={(event) => patchRule(rule.stage, { task_title: event.target.value })}
              />
              <Input
                label="Due hours after stage entry"
                tooltip={FIELD_HINTS.cycle_rule_due_hours}
                type="number"
                min={1}
                max={720}
                value={String(rule.due_hours)}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (!Number.isFinite(next)) return;
                  patchRule(rule.stage, { due_hours: next });
                }}
              />
              {rule.queue_email_draft && (
                <>
                  <Input
                    label="Email subject"
                    tooltip={FIELD_HINTS.cycle_rule_email_subject}
                    value={rule.email_subject ?? ""}
                    onChange={(event) => patchRule(rule.stage, { email_subject: event.target.value })}
                  />
                  <Input
                    label="Email body"
                    tooltip={FIELD_HINTS.cycle_rule_email_body}
                    value={rule.email_body ?? ""}
                    onChange={(event) => patchRule(rule.stage, { email_body: event.target.value })}
                  />
                </>
              )}
            </Stack>
          ))}
          <div className="crm-form-actions">
            <Button type="button" disabled={savingRules || rules.length === 0} onClick={() => saveRules()}>
              {savingRules ? "Saving…" : "Save stage-entry rules"}
            </Button>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}

function TemplateEditor({
  template,
  saving,
  dirty,
  readOnly = false,
  onAdd,
  onChange,
  onRemove,
  onSave,
}: {
  template: DraftTemplate;
  saving: boolean;
  dirty: boolean;
  readOnly?: boolean;
  onAdd: (stageId: string) => void;
  onChange: (stageId: string, key: string, patch: Partial<DraftTask>) => void;
  onRemove: (stageId: string, task: DraftTask) => void;
  onSave: () => void;
}) {
  const plan = useMemo(() => buildPlan(template), [template]);
  return (
    <Card>
      <Stack gap={4}>
        <h2>{template.name}</h2>
        <section aria-label="Task estimates">
          <h3>All tasks</h3>
          <p className="crm-muted">
            Cycle length {plan.totalLabel}
            {" · "}
            {plan.activeCount} active {plan.activeCount === 1 ? "task" : "tasks"}
            {plan.inactiveCount > 0 ? ` · ${plan.inactiveCount} inactive` : ""}
          </p>
          <p className="crm-muted">
            Numbers show the order on the cycle. Every active task in a stage still needs to be done.
          </p>
          <ol className="crm-cycle-plan">
            {plan.rows.map((row) => (
              <li key={row.key}>
                <span>
                  <strong>{row.step} {row.stageName}.</strong> {row.title}
                </span>
                <span className="crm-muted">
                  {row.estimate} · completes {row.cumulative} from the start
                </span>
              </li>
            ))}
          </ol>
        </section>
        <SaveTasksButton saving={saving} dirty={dirty} readOnly={readOnly} onSave={onSave} />
        {template.stages.map((stage, index) => {
          const visible = stage.tasks.filter((task) => !task.deleted);
          const active = visible.filter((task) => task.is_active);
          const inactive = visible.filter((task) => !task.is_active);
          const stageHours = active.reduce(
            (sum, task) => sum + task.estimate_amount * ESTIMATE_UNIT_HOURS[task.estimate_unit],
            0,
          );
          return (
            <section key={stage.id} className="crm-cycle-stage-editor" aria-label={stage.name}>
              <h3>{index + 1}. {stage.name}</h3>
              <p className="crm-muted" title={stageSlaTooltip(stage.name)}>
                Stage length {formatHourSpan(stageHours)}. Later stages start after this.
              </p>
              <Stack gap={3}>
                {active.map((task, taskIndex) => (
                  <TaskEditor
                    key={task.key}
                    step={`${index + 1}.${taskIndex + 1}`}
                    task={task}
                    onChange={(patch) => onChange(stage.id, task.key, patch)}
                    onDeactivate={() => onChange(stage.id, task.key, { is_active: false })}
                  />
                ))}
              </Stack>
              <Button type="button" variant="secondary" size="sm" onClick={() => onAdd(stage.id)}>
                Add task to {stage.name}
              </Button>
              {inactive.length > 0 && (
                <Stack gap={2}>
                  <h4>Not used in this cycle</h4>
                  {inactive.map((task) => (
                    <div key={task.key} className="crm-cycle-task-edit is-inactive">
                      <strong>{task.title || "Untitled task"}</strong>
                      <p className="crm-muted">{estimateLabel(task.estimate_amount, task.estimate_unit)}</p>
                      <Cluster gap={2}>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => onChange(stage.id, task.key, { is_active: true })}
                        >
                          Restore {task.title || "task"}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(stage.id, task)}>
                          Delete {task.title || "task"}
                        </Button>
                      </Cluster>
                    </div>
                  ))}
                </Stack>
              )}
            </section>
          );
        })}
        <SaveTasksButton saving={saving} dirty={dirty} readOnly={readOnly} onSave={onSave} />
      </Stack>
    </Card>
  );
}

function SaveTasksButton({
  saving,
  dirty,
  readOnly = false,
  onSave,
}: {
  saving: boolean;
  dirty: boolean;
  readOnly?: boolean;
  onSave: () => void;
}) {
  return (
    <div className="crm-form-actions">
      <Button type="button" disabled={readOnly || saving || !dirty} onClick={onSave}>
        {readOnly ? "Organization admin only" : saving ? "Saving…" : dirty ? "Save tasks" : "Tasks saved"}
      </Button>
    </div>
  );
}

function TaskEditor({
  step,
  task,
  onChange,
  onDeactivate,
}: {
  step: string;
  task: DraftTask;
  onChange: (patch: Partial<DraftTask>) => void;
  onDeactivate: () => void;
}) {
  const name = task.title.trim() || "new task";
  return (
    <div className="crm-cycle-task-edit">
      <strong>{step}</strong>
      <Input
        label="Task"
        tooltip={FIELD_HINTS.cycle_task_title}
        aria-label={`Task title: ${name}`}
        value={task.title}
        onChange={(event) => onChange({ title: event.target.value })}
      />
      <div className="crm-cycle-estimate">
        <Input
          label="Estimate"
          tooltip={FIELD_HINTS.cycle_task_estimate}
          aria-label={`Estimate for ${name}`}
          type="number"
          min={1}
          max={999}
          value={String(task.estimate_amount)}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (!Number.isFinite(next)) return;
            onChange({ estimate_amount: Math.min(999, Math.max(1, Math.round(next))) });
          }}
        />
        <label className="crm-field-label">
          Unit
          <select
            className="crm-select"
            aria-label={`Estimate unit for ${name}`}
            title={FIELD_HINTS.cycle_task_unit}
            value={task.estimate_unit}
            onChange={(event) => {
              if (isEstimateUnit(event.target.value)) onChange({ estimate_unit: event.target.value });
            }}
          >
            {ESTIMATE_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" variant="ghost" size="sm" title={FIELD_HINTS.cycle_task_active} onClick={onDeactivate}>
          Deactivate {name}
        </Button>
      </div>
    </div>
  );
}

function buildPlan(template: DraftTemplate) {
  const rows: Array<{
    key: string;
    step: string;
    stageName: string;
    title: string;
    estimate: string;
    cumulative: string;
  }> = [];
  let cursor = 0;
  let inactiveCount = 0;
  template.stages.forEach((stage, index) => {
    let taskIndex = 0;
    for (const task of stage.tasks) {
      if (task.deleted) continue;
      if (!task.is_active) {
        inactiveCount += 1;
        continue;
      }
      taskIndex += 1;
      const hours = task.estimate_amount * ESTIMATE_UNIT_HOURS[task.estimate_unit];
      cursor += hours;
      rows.push({
        key: task.key,
        step: `${index + 1}.${taskIndex}`,
        stageName: stage.name,
        title: task.title.trim() || "Untitled task",
        estimate: estimateLabel(task.estimate_amount, task.estimate_unit),
        cumulative: formatHourSpan(cursor),
      });
    }
  });
  return {
    rows,
    totalLabel: formatHourSpan(cursor),
    activeCount: rows.length,
    inactiveCount,
  };
}
